import { Box } from '@mui/material'

/**
 * S37 (historia 107, CA7/RN14/DG-5/DG-7): the cluster send answers with ONE outcome per fabrication
 * order (`{ fabricationOrderCode, targetNodeName, state, reasonCode, reasonMessage }`, Sun's
 * `SyncOutcomeDto`), so the alert has to show successes, pending sends and failures at the same time
 * and must never claim success for an order the target did not apply.
 *
 * ⚠️ RN15: only `DELIVERED` means the order ended up usable in the destination node. `ACCEPTED` is
 * "validated and handed to the cluster channel, with the target yet to answer": it is NOT a
 * delivery, so it gets its own category instead of being counted as a success.
 *
 * ⚠️ The reason shown to the user is `reasonCode`, a STABLE i18n key of Sun (e.g.
 * `FABRICATION_ORDER_NOT_SENDABLE`), translated here. `reasonMessage` is developer-facing detail
 * that names the offending order or node and is explicitly "not for display": it never reaches the
 * user. This is also what dodges R10 (jupiter#2848) — a server-composed message cannot be resolved
 * by exact key match, a stable code can.
 */

const DELIVERED = 'DELIVERED'
const ACCEPTED = 'ACCEPTED'
// S118 (historia 108, INV33): la célula no contestó — apagada, red, tiempo de espera. La petición
// queda pendiente y SE REINTENTA SOLA, así que no es un fallo y no puede presentarse como tal. Es lo
// que la separa de REJECTED, donde la célula dijo que no y esperar no arregla nada.
const NOT_DELIVERED = 'NOT_DELIVERED'

/**
 * The code of a fabrication order inside an outcome.
 *
 * @param {Object} outcome Per-order outcome of the server
 * @returns {string} The fabrication order code, or '' when the outcome carries none
 */
const codeOf = (outcome) => outcome?.fabricationOrderCode || ''

/**
 * One readable line per rejected fabrication order: `<code>: <translated reasonCode>`.
 *
 * @param {Object} outcome Per-order outcome of the server
 * @param {Function} t_ Translation function
 * @returns {string} The line shown to the user
 */
const failureLine = (outcome, t_) => {
  const code = codeOf(outcome)
  const reason = outcome?.reasonCode || ''
  return reason === '' ? code : code + ': ' + t_(reason)
}

/**
 * A `<label>: <code, code, ...>` line naming every order of one category.
 *
 * @param {string} label Already translated label of the category
 * @param {Object[]} outcomes Outcomes of that category
 * @returns {string} The line shown to the user
 */
const categoryLine = (label, outcomes) => label + ': ' + outcomes.map(codeOf).join(', ')

/**
 * Renders the lines of the alert message, one per line, left aligned.
 *
 * @param {string[]} lines Lines to render
 * @returns {JSX.Element} The message block
 */
const messageBlock = (lines) => (
  <Box component='span' sx={{ display: 'block', textAlign: 'left' }}>
    {lines.map((line, index) => (
      <Box component='span' sx={{ display: 'block' }} key={index + '-' + line}>{line}</Box>
    ))}
  </Box>
)

/**
 * Builds the alert of a cluster send from the per-order outcomes returned by the server.
 *
 * @param {Object[]} outcomes Per-order outcomes (Sun's `SyncOutcomeDto`)
 * @param {Function} t_ Translation function
 * @returns {Object} Alert descriptor for showAlert (`{ severity, title, message }`)
 */
export const buildClusterSendAlert = (outcomes, t_) => {
  const perOrderOutcomes = Array.isArray(outcomes) ? outcomes : []
  const delivered = perOrderOutcomes.filter((outcome) => outcome?.state === DELIVERED)
  const accepted = perOrderOutcomes.filter((outcome) => outcome?.state === ACCEPTED)
  const notDelivered = perOrderOutcomes.filter((outcome) => outcome?.state === NOT_DELIVERED)
  // Anything else did not end up usable in the target AND will not fix itself: REJECTED, and also a
  // state this client does not know, which it must never read as a success.
  const failed = perOrderOutcomes.filter((outcome) =>
    outcome?.state !== DELIVERED && outcome?.state !== ACCEPTED && outcome?.state !== NOT_DELIVERED)
  // The two categories that resolve on their own. INV33: neither may be presented as a failure.
  const pending = accepted.concat(notDelivered)

  // RN15/DG-5: a response that carries no per-order outcome proves NO delivery, so it can never be
  // reported as a success -- that is exactly the "a warning looks like a success" defect (R1) this
  // historia has to avoid. An empty list, or a body that is not the agreed array, is a failure.
  if (perOrderOutcomes.length === 0) {
    return { severity: 'error', title: t_('Error sending fabrication orders:'), message: '' }
  }

  if (failed.length === 0 && pending.length === 0) {
    return { severity: 'success', title: t_('Fabrication orders sent correctly'), message: '' }
  }

  const deliveredLines = delivered.length === 0
    ? []
    : [categoryLine(t_('Fabrication orders sent correctly'), delivered)]
  const acceptedLines = accepted.length === 0
    ? []
    : [categoryLine(t_('Pending confirmation from the destination node'), accepted)]
  // S118: su propia línea, y con el "se reintentará" dicho, porque es justo lo que separa esperar de
  // tener que actuar.
  const notDeliveredLines = notDelivered.length === 0
    ? []
    : [categoryLine(t_('Not delivered. It will be retried'), notDelivered)]

  // RN15: everything was handed to the channel and nothing failed, but nothing is confirmed
  // delivered either. It is neither a success nor a failure, so it is reported as information.
  if (failed.length === 0) {
    return {
      severity: 'info',
      title: t_('Fabrication orders handed to the cluster channel'),
      message: messageBlock(deliveredLines.concat(acceptedLines).concat(notDeliveredLines))
    }
  }

  const failureLines = failed.map((outcome) => failureLine(outcome, t_))

  if (delivered.length === 0 && pending.length === 0) {
    return {
      severity: 'error',
      title: t_('Error sending fabrication orders:'),
      message: messageBlock(failureLines)
    }
  }

  return {
    severity: 'warning',
    title: t_('Some fabrication orders could not be sent'),
    message: messageBlock(deliveredLines.concat(acceptedLines).concat(notDeliveredLines).concat(failureLines))
  }
}

export default buildClusterSendAlert
